/**
 * The browser's one database (IndexedDB through Dexie). Everything in it is
 * local to this browser and content-bearing; "clear all data" deletes it whole.
 *
 *   stages  — model-call and stage-output caches keyed by content hash
 *   runs    — the reconstruction (ledger.json) of each completed run
 *   scores  — study-mode judgments per card (enums only), keyed by cutoff
 *   corrections — the append-only correction log (append and list only; correctionLog.ts)
 *   product_actions       — one row per action on an active card (M3.6b)
 *   history_confirmations — one row per Probably done? answer, counted apart
 *
 * The last two are the local telemetry queue: content-free rows waiting for
 * the endpoint M5.3 wires. They are separate tables because the Spec measures
 * them separately — a history confirmation is never a ProductAction.
 *
 * The participant's model key is never written here, or anywhere.
 */
import Dexie from 'dexie'
import type { Table } from 'dexie'
import type { Correction, HistoryConfirmation, Ledger, ProductAction } from '@cordelia/core'
import type { Judgment } from '../study/fields.js'

export type StageRow = {
  readonly key: string
  readonly value: string
}

export type RunRow = {
  readonly id: string
  readonly cutoff: string
  readonly window_days: number
  readonly me: readonly string[]
  readonly completed_at: string
  readonly ledger: Ledger
}

export type ScoreRow = {
  readonly key: string
  readonly cutoff: string
  readonly item_id: string
  readonly judgment: Judgment
}

/** A queued event: the record plus the order it was queued in. */
export type ProductActionRow = ProductAction & { readonly seq?: number }
export type HistoryConfirmationRow = HistoryConfirmation & { readonly seq?: number }

export class CordeliaDb extends Dexie {
  declare stages: Table<StageRow, string>
  declare runs: Table<RunRow, string>
  declare scores: Table<ScoreRow, string>
  declare corrections: Table<Correction, string>
  declare product_actions: Table<ProductActionRow, number>
  declare history_confirmations: Table<HistoryConfirmationRow, number>

  constructor(name = 'cordelia') {
    super(name)
    this.version(1).stores({ stages: 'key' })
    this.version(2).stores({ stages: 'key', runs: 'id, completed_at' })
    this.version(3).stores({ stages: 'key', runs: 'id, completed_at', scores: 'key, cutoff' })
    this.version(4).stores({
      stages: 'key',
      runs: 'id, completed_at',
      scores: 'key, cutoff',
      corrections: 'id, time',
    })
    this.version(5).stores({
      stages: 'key',
      runs: 'id, completed_at',
      scores: 'key, cutoff',
      corrections: 'id, time',
      product_actions: '++seq, time, item_id',
      history_confirmations: '++seq, time, item_id',
    })
  }
}

let shared: CordeliaDb | null = null

/** One connection per context (the app, or the worker). */
export function db(): CordeliaDb {
  shared ??= new CordeliaDb()
  return shared
}

/** The only deletion path: everything, at once. */
export async function clearAllData(): Promise<void> {
  const current = shared ?? new CordeliaDb()
  await current.delete()
  shared = null
}
